/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import styles from './masthead.scss';
import './megamenu-link-with-icon';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * MegaMenu category group
 *
 * @element c4d-megamenu-category-group
 * @csspart shield - The shield element of the megamenu category group. Usage: `c4d-megamenu-category-group::part(shield)`
 * @csspart content-wrapper - The content wrapper of the megamenu category group. Usage: `c4d-megamenu-category-group::part(content-wrapper)`
 * @csspart heading - The heading of the megamenu category group. Usage: `c4d-megamenu-category-group::part(heading)`
 * @csspart content - The content of the megamenu category group. Usage: `c4d-megamenu-category-group::part(content)`
 */
@customElement(`${c4dPrefix}-megamenu-category-group`)
class C4DMegaMenuCategoryGroup extends LitElement {
  /**
   * href for the category group title
   */
  @property({ reflect: true })
  href = '';

  /**
   * category group title/heading
   */
  @property({ reflect: true })
  title = '';

  render() {
    return html`
      <div
        part="shield"
        class="${prefix}--masthead__megamenu__category-group-shield">
        <div
          part="content-wrapper"
          class="${prefix}--masthead__megamenu__category-group-content-wrapper">
          <div
            part="heading"
            class="${prefix}--masthead__megamenu__category-group-heading">
            <slot name="heading"></slot>
          </div>
          <div
            part="content"
            class="${prefix}--masthead__megamenu__category-group-content">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

export default C4DMegaMenuCategoryGroup;
